import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice } from "@/lib/api";
import { CATEGORY_FILTERS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { motion } from "framer-motion";
import {
  ArrowLeft, Beef, Bike, Cake, Carrot, ChevronLeft, ChevronRight, Clock, Croissant, LayoutGrid,
  MapPin, Pill, Search, Shirt, ShoppingBasket, Smartphone, Star, Store,
  Tag, Utensils,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const SHOP_FALLBACK = "/image-placeholder.svg";

const CAT_ICONS = {
  "الكل": LayoutGrid, "مطاعم": Utensils, "بقالة": ShoppingBasket, "خضار وفواكه": Carrot,
  "حلويات": Cake, "لحوم ودواجن": Beef, "مخابز": Croissant, "صيدلية": Pill,
  "إلكترونيات": Smartphone, "ملابس": Shirt, "عام": Store,
};

const dtime = (id) => {
  let h = 0;
  for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) % 100000;
  const base = 15 + (h % 16);
  return `${base}-${base + 10}`;
};

export default function BuyerHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shops, setShops] = useState([]);
  const [offers, setOffers] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cat, setCat] = useState("الكل");
  const shopsSectionRef = useRef(null);
  const mobileBrowseRef = useRef(null);
  const [browseSection, setBrowseSection] = useState("shops");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 220);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    const loadShops = async () => {
      setLoading(true);
      try {
        const [{ data }, productResponse] = await Promise.all([
          api.get("/shops", { params: { search: debouncedSearch, category: cat } }),
          api.get("/products", { params: { search: debouncedSearch, category: cat } }),
        ]);
        if (active) { setShops(data); setProducts(productResponse.data); }
      } catch {
        if (active) setShops([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadShops();
    const refresh = () => { if (!document.hidden) loadShops(); };
    window.addEventListener("focus", refresh);
    return () => { active = false; window.removeEventListener("focus", refresh); };
  }, [debouncedSearch, cat]);

  useEffect(() => {
    api.get("/offers").then((response) => setOffers(response.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/hero-banners")
      .then(({ data }) => setHeroBanners(Array.isArray(data) ? data.filter((banner) => banner.image_path) : []))
      .catch((error) => console.error("تعذر تحميل شرائح إعلانات الصفحة الرئيسية:", error));
  }, []);

  useEffect(() => {
    setActiveBanner(0);
    if (heroBanners.length < 2) return undefined;
    const interval = window.setInterval(() => {
      setActiveBanner((current) => (current + 1) % heroBanners.length);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [heroBanners.length]);

  const showPreviousBanner = () => setActiveBanner((current) => (current - 1 + heroBanners.length) % heroBanners.length);
  const showNextBanner = () => setActiveBanner((current) => (current + 1) % heroBanners.length);

  const firstName = user?.name?.trim()?.split(/\s+/)[0] || "صديقنا";
  const browseShops = (event) => {
    event?.preventDefault();
    setBrowseSection("shops");
    const target = window.matchMedia("(max-width: 767px)").matches
      ? mobileBrowseRef.current
      : shopsSectionRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="buyer-home">
      <section className="mb-4" aria-label="البنرات الإعلانية">
        {heroBanners.length > 0 ? (
          <div className="relative mx-auto w-[92%] max-w-[960px] overflow-hidden rounded-[24px] bg-muted sm:w-[88%]" role="region" aria-roledescription="carousel" aria-label="شرائح إعلانية">
            <img
              key={heroBanners[activeBanner]?.image_path}
              src={fileUrl(heroBanners[activeBanner]?.image_path)}
              alt={`شريحة إعلانية ${activeBanner + 1}`}
              fetchPriority="high"
              className="block h-auto w-full"
            />
            {heroBanners.length > 1 && (
              <>
                <button type="button" aria-label="الشريحة السابقة" onClick={showPreviousBanner}
                  className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:right-4">
                  <ChevronRight className="h-5 w-5" />
                </button>
                <button type="button" aria-label="الشريحة التالية" onClick={showNextBanner}
                  className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/30 text-white backdrop-blur-sm transition hover:bg-black/50 sm:left-4">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
                  {heroBanners.map((banner, index) => (
                    <button key={`${banner.image_path}-dot`} type="button"
                      aria-label={`عرض الشريحة ${index + 1}`} aria-current={index === activeBanner ? "true" : undefined}
                      onClick={() => setActiveBanner(index)}
                      className={`h-2 rounded-full transition-all ${index === activeBanner ? "w-6 bg-white" : "w-2 bg-white/65 hover:bg-white"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-start justify-center rounded-[24px] bg-brand px-4 py-4 text-brand-foreground sm:px-8 sm:py-9">
            <span className="hidden items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-bold text-white/85 sm:inline-flex">
              <MapPin className="h-3.5 w-3.5" /> كفرنبل وريفها
            </span>
            <h1 className="text-lg font-extrabold leading-tight tracking-tight text-white sm:mt-4 sm:text-3xl">
              أهلًا {firstName}، شو بدك تطلب اليوم؟
            </h1>
            <p className="mt-2 hidden max-w-md text-sm leading-6 text-white/75 sm:block">
              ابحث عن متجر أو منتج، وشوف الخيارات المتاحة للتوصيل.
            </p>
            <button
              type="button"
              onClick={browseShops}
              className="mt-5 hidden h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-extrabold text-brand transition-colors hover:bg-white/90 sm:inline-flex"
            >
              تصفح المتاجر <ArrowLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </section>

      <section className="mb-5" aria-label="البحث عن متجر أو منتج">
        <form onSubmit={browseShops} className="mx-auto flex max-w-3xl flex-col justify-center rounded-[20px] bg-card p-3 shadow-card sm:p-5">
          <label htmlFor="buyer-home-search" className="mb-3 hidden text-sm font-extrabold sm:block">
            ابحث عن متجر أو منتج
          </label>
          <div className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <Search data-testid="shop-search-icon" className="absolute right-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-primary" />
              <Input
                id="buyer-home-search"
                data-testid="shop-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="اسم متجر، وجبة أو منتج..."
                className="h-12 rounded-2xl border border-brand/10 bg-background pe-12 ps-4 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/25"
              />
            </div>
            <button type="submit" aria-label="بحث" className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 sm:px-5">
              <span className="hidden sm:inline">بحث</span> <Search className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 hidden text-xs leading-5 text-muted-foreground sm:block">
            النتائج تتحدث تلقائيًا أثناء الكتابة، أو اضغط «بحث» للانتقال إليها.
          </p>
        </form>
      </section>

      <section className="mt-4 lg:mt-10" aria-label="فئات المتاجر">
        <div className="mb-2 flex items-end justify-between gap-4 sm:mb-4">
          <div>
            <span className="hidden text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary sm:block">اكتشف الأقسام</span>
            <h2 className="text-sm font-extrabold tracking-tight sm:mt-1 sm:text-2xl">تصفّح حسب القسم</h2>
          </div>
          <span className="hidden text-xs font-bold text-muted-foreground sm:block">اختر قسمًا لعرض المتاجر والمنتجات المناسبة</span>
        </div>
        <div className="no-scrollbar -mx-4 flex gap-2.5 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0">
          {CATEGORY_FILTERS.map((category) => {
            const Icon = category === "مختارات اليوم" ? Tag : (CAT_ICONS[category] || Store);
            const active = cat === category;
            return (
              <button
                key={category}
                data-testid={`cat-${category}`}
                onClick={() => setCat(category)}
                aria-pressed={active}
                className={`inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 text-xs font-extrabold transition-all sm:h-12 sm:px-5 sm:text-sm ${active ? "bg-brand text-brand-foreground shadow-brand" : "bg-secondary text-muted-foreground hover:-translate-y-0.5 hover:bg-card hover:text-foreground hover:shadow-card"}`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{category}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section ref={mobileBrowseRef} className="mt-3 scroll-mt-20 md:hidden" aria-label="تصفّح المحتوى">
        <div className="mb-3 grid grid-cols-3 rounded-2xl bg-muted p-1">
          {[
            ["shops", "متاجر", Store],
            ["products", "منتجات", ShoppingBasket],
            ["offers", "عروض", Tag],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setBrowseSection(id)}
              aria-pressed={browseSection === id}
              className={`flex min-h-10 items-center justify-center gap-1.5 rounded-xl text-xs font-extrabold transition-colors ${
                browseSection === id ? "bg-card text-primary shadow-sm" : "text-muted-foreground"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {browseSection === "shops" && (
          loading ? (
            <div className="space-y-2" aria-label="جار تحميل المتاجر">
              {[0, 1, 2].map((item) => <div key={item} className="h-[88px] animate-pulse rounded-2xl bg-secondary" />)}
            </div>
          ) : shops.length === 0 ? (
            <div className="rounded-2xl bg-muted px-4 py-10 text-center text-sm font-bold text-muted-foreground">
              <Store className="mx-auto mb-2 h-7 w-7 opacity-40" />
              لا توجد متاجر مطابقة
            </div>
          ) : (
            <div className="space-y-2">
              {shops.map((shop) => (
                <button
                  key={shop.id}
                  type="button"
                  data-testid={`mobile-shop-card-${shop.id}`}
                  onClick={() => navigate(`/app/shop/${shop.id}`)}
                  className="flex min-h-[88px] w-full items-center gap-3 rounded-2xl bg-card p-2.5 text-start shadow-card active:scale-[0.99]"
                >
                  <img src={shop.image_path ? fileUrl(shop.image_path) : SHOP_FALLBACK} alt="" className="h-[68px] w-[68px] shrink-0 rounded-xl bg-muted object-cover" loading="lazy" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-extrabold">{shop.name}</span>
                      <span className="flex shrink-0 items-center gap-1 text-xs font-bold"><Star className="h-3.5 w-3.5 fill-rating text-rating" />{shop.rating > 0 ? shop.rating : "جديد"}</span>
                    </span>
                    <span className="mt-1 block truncate text-xs text-muted-foreground">{shop.category} · التوصيل {fmtPrice(shop.delivery_fee)}</span>
                    <span className={`mt-1 block text-[10px] font-bold ${shop.is_open ? "text-success" : "text-destructive"}`}>{shop.is_open ? "مفتوح الآن" : "مغلق الآن"}</span>
                  </span>
                </button>
              ))}
            </div>
          )
        )}

        {browseSection === "products" && (
          products.length === 0 ? (
            <div className="rounded-2xl bg-muted px-4 py-10 text-center text-sm font-bold text-muted-foreground">لا توجد منتجات مطابقة</div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {products.slice(0, 12).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} onOpen={() => navigate(`/app/product/${product.id}`)} />
              ))}
            </div>
          )
        )}

        {browseSection === "offers" && (
          offers.length === 0 ? (
            <div className="rounded-2xl bg-muted px-4 py-10 text-center text-sm font-bold text-muted-foreground">لا توجد عروض حاليًا</div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {offers.slice(0, 12).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} onOpen={() => navigate(`/app/product/${product.id}`)} />
              ))}
            </div>
          )
        )}
      </section>

      {offers.length > 0 && (cat === "الكل" || cat === "مختارات اليوم") && (
        <section className="mt-9 hidden md:block lg:mt-12" data-testid="offers-strip">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary">مختارات اليوم</span>
              <h2 className="mt-1 text-xl font-extrabold tracking-tight sm:text-2xl">عروض لا تفوّت</h2>
            </div>
            <span className="flex items-center gap-1 text-xs font-bold text-muted-foreground">
              لفترة محدودة <ArrowLeft className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:gap-x-5 md:grid-cols-3 xl:grid-cols-4">
            {offers.slice(0, 8).map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
                onOpen={() => navigate(`/app/product/${product.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="mt-8 hidden rounded-3xl bg-muted p-5 md:block" aria-label="طريقة الطلب">
        <h2 className="font-extrabold">كيف تطلب؟</h2>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">اختر متجرًا ومنتجاتك، ثم أضف عنوانك وأرسل الطلب من السلة. يقبل البائع الطلب ويجهّزه، ثم يستلمه السائق ويوصله إليك. تابع الحالة في «طلباتي» وادفع نقدًا عند الاستلام.</p>
      </section>
      {products.length > 0 && (
        <section className="mt-10 hidden md:block" data-testid="home-products">
          <h2 className="mb-5 text-xl font-extrabold">منتجات المتاجر</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
            {products.map((product, index) => <ProductCard key={product.id} product={product} index={index} onOpen={() => navigate(`/app/product/${product.id}`)} />)}
          </div>
        </section>
      )}
      <section ref={shopsSectionRef} className="mt-10 hidden scroll-mt-24 md:block lg:mt-14">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary">قريب منك</span>
            <h2 className="mt-1 text-xl font-extrabold tracking-tight sm:text-2xl">
              {cat === "الكل" ? "متاجر تستحق التجربة" : cat === "مختارات اليوم" ? "متاجر لديها عروض اليوم" : `متاجر ${cat}`}
            </h2>
          </div>
          <span className="text-xs font-bold text-muted-foreground">{shops.length} متجر</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((item) => <div key={item} className="h-72 animate-pulse rounded-[30px] bg-secondary" />)}
          </div>
        ) : shops.length === 0 ? (
          <div className="rounded-[30px] bg-muted py-20 text-center text-muted-foreground">
            <Store className="mx-auto h-11 w-11 opacity-35" />
            <p className="mt-3 text-sm font-bold">لا توجد متاجر مطابقة</p>
            <button onClick={() => { setSearch(""); setCat("الكل"); }} className="mt-3 text-xs font-bold text-primary">إعادة ضبط البحث</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {shops.map((shop, index) => (
              <motion.button
                key={shop.id}
                data-testid={`shop-card-${shop.id}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.28) }}
                onClick={() => navigate(`/app/shop/${shop.id}`)}
                className="group overflow-hidden rounded-[28px] bg-card text-start shadow-card transition-all hover:-translate-y-1 hover:shadow-card-hover"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-secondary">
                  <img
                    src={shop.image_path ? fileUrl(shop.image_path) : SHOP_FALLBACK}
                    alt={shop.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand/60 via-transparent to-transparent" />
                  <span className="absolute right-3 top-3 rounded-full bg-card/90 px-3 py-1 text-[10px] font-extrabold text-brand backdrop-blur-sm">{shop.category}</span>
                  <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-brand/75 px-2.5 py-1 text-[11px] font-bold text-brand-foreground backdrop-blur-sm">
                    <Star className="h-3 w-3 fill-rating text-rating" />
                    {shop.rating > 0 ? shop.rating : "جديد"}
                  </span>
                  {!shop.is_open && <div className="absolute inset-0 grid place-items-center bg-brand/60 text-xs font-extrabold text-brand-foreground">مغلق الآن</div>}
                </div>
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="line-clamp-1 text-base font-extrabold sm:text-lg">{shop.name}</h3>
                      <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{shop.description || "متجر محلي في كفرنبل"}</p>
                    </div>
                    <ArrowLeft className="mt-1 h-5 w-5 shrink-0 transition-transform group-hover:-translate-x-1" />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-brand/10 pt-3 text-[11px] font-bold">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" /> {dtime(shop.id)} د</span>
                    <span className="flex items-center gap-1 text-muted-foreground"><Bike className="h-3.5 w-3.5" /> {fmtPrice(shop.delivery_fee)}</span>
                    <span className="mr-auto flex items-center gap-1 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> قريب منك</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
